<template>
  <AppLayout title="Adresse bearbeiten">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">
                {{ address.id ? $t('Adresse bearbeiten') : $t('Neue Adresse erstellen') }}
              </h1>
              <p class="text-sm text-gray-600 mt-1">
                {{ address.id ? $t('Aktualisieren Sie Ihre Adressinformationen') : $t('Fügen Sie eine neue Geschäfts- oder Lieferadresse hinzu') }}
              </p>
            </div>
            <Link 
              :href="route('customerAccount')" 
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {{ $t('Zurück') }}
            </Link>
          </div>
        </div>
      </div>

      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Success Message -->
        <div v-if="$page.props.flash?.success" class="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-green-700">{{ $page.props.flash.success }}</p>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="Object.keys(errors).length > 0" class="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">{{ $t('Es gab Fehler bei Ihrer Eingabe') }}</h3>
              <div class="mt-2 text-sm text-red-700">
                <ul class="list-disc pl-5 space-y-1">
                  <li v-for="(error, field) in errors" :key="field">{{ error }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="saveAddress" class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('Adressinformationen') }}</h2>
          </div>
          
          <div class="p-6 space-y-6">
            <!-- Address Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">{{ $t('Adresstyp') }}</label>
              <div class="space-y-2">
                <label class="flex items-center">
                  <input
                    v-model="form.is_shipping_address"
                    type="checkbox"
                    class="rounded border-gray-300 text-sonnenglas-600 focus:ring-sonnenglas-500"
                  />
                  <span class="ml-2 text-sm text-gray-700">{{ $t('Lieferadresse') }}</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="form.is_store"
                    type="checkbox"
                    class="rounded border-gray-300 text-sonnenglas-600 focus:ring-sonnenglas-500"
                  />
                  <span class="ml-2 text-sm text-gray-700">{{ $t('Geschäft / Showroom') }}</span>
                </label>
              </div>
            </div>

            <!-- Company & Contact -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div class="sm:col-span-2">
                <label for="company" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('Firmenname') }}
                  <span class="text-red-500">*</span>
                </label>
                <input
                  id="company"
                  v-model="form.company"
                  type="text"
                  required
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>

              <div>
                <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('Vorname') }}
                </label>
                <input
                  id="first_name"
                  v-model="form.first_name"
                  type="text"
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>

              <div>
                <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('Nachname') }}
                </label>
                <input
                  id="last_name"
                  v-model="form.last_name"
                  type="text"
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>
            </div>

            <!-- Street Address -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div class="sm:col-span-2">
                <label for="street_name" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('Straße') }}
                  <span class="text-red-500">*</span>
                </label>
                <input
                  id="street_name"
                  v-model="form.street_name"
                  type="text"
                  required
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>

              <div>
                <label for="street_no" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('Hausnummer') }}
                  <span class="text-red-500">*</span>
                </label>
                <input
                  id="street_no"
                  v-model="form.street_no"
                  type="text"
                  required
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>
            </div>

            <!-- City & Postal -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label for="zip" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('PLZ') }}
                  <span class="text-red-500">*</span>
                </label>
                <input
                  id="zip"
                  v-model="form.zip"
                  type="text"
                  required
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>

              <div class="sm:col-span-2">
                <label for="city" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('Stadt') }}
                  <span class="text-red-500">*</span>
                </label>
                <input
                  id="city"
                  v-model="form.city"
                  type="text"
                  required
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>
            </div>

            <!-- Phone Number -->
            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('Telefonnummer') }}
              </label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                :placeholder="$t('z.B. +49 123 456789')"
              />
            </div>

            <!-- Store-specific fields -->
            <div v-if="form.is_store" class="space-y-6 pt-6 border-t border-gray-200">
              <h3 class="text-sm font-semibold text-gray-900">{{ $t('Geschäftsinformationen') }}</h3>
              
              <div>
                <label for="website" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('Webseite') }}
                </label>
                <input
                  id="website"
                  v-model="form.website"
                  type="url"
                  placeholder="https://www.example.com"
                  class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-3">{{ $t('Öffnungszeiten') }}</label>
                <div class="space-y-3">
                  <div v-for="day in weekDays" :key="day.value" class="grid grid-cols-3 gap-3 items-center">
                    <label class="text-sm text-gray-700">{{ day.label }}</label>
                    <input
                      v-model="form.hours[day.value].from"
                      type="time"
                      class="block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                    />
                    <input
                      v-model="form.hours[day.value].to"
                      type="time"
                      class="block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Information -->
            <div>
              <label for="information" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('Zusätzliche Informationen') }}
              </label>
              <textarea
                id="information"
                v-model="form.information"
                rows="3"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                :placeholder="$t('z.B. Lieferanweisungen, Gebäudecode, etc.')"
              />
            </div>
          </div>

          <!-- Form Actions -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
            <div v-if="address.id && form.is_shipping_address">
              <button
                type="button"
                @click="deleteAddress"
                class="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {{ $t('Adresse löschen') }}
              </button>
            </div>
            <div v-else>&nbsp;</div>
            
            <div class="flex space-x-3">
              <Link
                :href="route('customerAccount')"
                class="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
              >
                {{ $t('Abbrechen') }}
              </Link>
              <button
                type="submit"
                :disabled="isProcessing"
                class="px-4 py-2 bg-sonnenglas-600 text-white text-sm font-medium rounded-md hover:bg-sonnenglas-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isProcessing" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ $t('Speichern...') }}
                </span>
                <span v-else>{{ address.id ? $t('Änderungen speichern') : $t('Adresse erstellen') }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, reactive, getCurrentInstance, inject } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const instance = getCurrentInstance()
const $t = instance?.appContext.config.globalProperties.$t || ((key: string) => key)
const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

interface Address {
  id?: number
  company: string
  first_name: string
  last_name: string
  street_name: string
  street_no: string
  zip: string
  city: string
  county?: string
  information?: string
  phone?: string
  is_shipping_address: boolean
  is_invoice_address: boolean
  is_store: boolean
  is_default: boolean
  hours?: Record<string, { from: string; to: string }>
  website?: string
}

interface Props {
  address: Address
}

const props = defineProps<Props>()

const weekDays = [
  { value: 'monday', label: $t('Montag') },
  { value: 'tuesday', label: $t('Dienstag') },
  { value: 'wednesday', label: $t('Mittwoch') },
  { value: 'thursday', label: $t('Donnerstag') },
  { value: 'friday', label: $t('Freitag') },
  { value: 'saturday', label: $t('Samstag') },
  { value: 'sunday', label: $t('Sonntag') },
]

const initializeHours = () => {
  const hours: Record<string, { from: string; to: string }> = {}
  weekDays.forEach(day => {
    hours[day.value] = props.address.hours?.[day.value] || { from: '', to: '' }
  })
  return hours
}

const form = reactive({
  company: props.address.company || '',
  first_name: props.address.first_name || '',
  last_name: props.address.last_name || '',
  street_name: props.address.street_name || '',
  street_no: props.address.street_no || '',
  zip: props.address.zip || '',
  city: props.address.city || '',
  county: props.address.county || '',
  information: props.address.information || '',
  phone: props.address.phone || '',
  is_shipping_address: props.address.is_shipping_address || false,
  is_store: props.address.is_store || false,
  hours: initializeHours(),
  website: props.address.website || '',
})

const errors = ref<Record<string, string>>({})
const isProcessing = ref(false)

const saveAddress = () => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  errors.value = {}
  isProcessing.value = true

  const url = props.address.id
    ? route('address.update', props.address.id)
    : route('address.store')

  router.post(url, form, {
    preserveState: true,
    preserveScroll: true,
    onError: (errorBag) => {
      errors.value = errorBag
    },
    onFinish: () => {
      isProcessing.value = false
    }
  })
}

const deleteAddress = () => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  if (confirm($t('Sind Sie sicher, dass Sie diese Adresse löschen möchten?'))) {
    router.delete(route('address.destroy', props.address.id), {
      preserveState: false,
      preserveScroll: false
    })
  }
}
</script>